<template>
  <div class="hotel-edit-accordion-4">
    <!-- TÍTULO -->
    <PmwHeading size="paragraph-80" weight="weight-light" class="form--title"
      >Modalidade de cobrança</PmwHeading
    >

    <div class="form--row">
      <!-- TIPO DE PAGAMENTO -->
      <PmwFormSelect
        :value="type"
        :error="errors.includes('TipoPagamento')"
        :options="formattedPaymentTypes"
        :search="true"
        :required="true"
        :infoIcon="true"
        :tooltipMinWidth="215"
        data-error="TipoPagamento"
        infoText="Define se o hóspede vai pagar pela hospedagem no ato da reserva, ou se os seus dados serão utilizados apenas como forma de garantia."
        label="Tipo de pagamento"
        placeholder="Selecione o tipo de pagamento"
        class="form--input form--input__select"
        id="form--type-payments"
        @input="$emit('changeType', $event)"
      />

      <!-- COBRANÇA -->
      <PmwFormSelect
        :value="charge"
        :error="errors.includes('TipoCobranca')"
        :options="formattedChargeList"
        :search="true"
        :required="true"
        :infoIcon="true"
        :disabled="disabledPayment"
        :class="{ 'form--input__disabled': disabledPayment }"
        data-error="TipoCobranca"
        tooltipMinWidth="202"
        infoText="Define o que será cobrado, de acordo com o tipo de pagamento."
        label="Cobrança"
        placeholder="Selecione a cobrança"
        class="form--input form--input__select"
        id="form--charge"
        @input="$emit('changeCharge', $event)"
      />
    </div>

    <!-- TÍTULO -->
    <PmwHeading size="paragraph-80" weight="weight-light" class="form--title"
      >Reembolso e cancelamento</PmwHeading
    >

    <div class="form--row">
      <!-- POLÍTICA DE CANCELAMENTO -->
      <PmwFormSelect
        :value="refundPolicy"
        :error="errors.includes('TipoPoliticaReembolso')"
        :options="formattedRefundPolicy"
        :search="true"
        :required="true"
        :infoIcon="true"
        :disabled="disabledPayment"
        :class="{ 'form--input__disabled': disabledPayment }"
        data-error="TipoPoliticaReembolso"
        tooltipMinWidth="202"
        infoText="Define se o pagamento efetuado pelo hóspede pode ou não ser reembolsado."
        label="Política de reembolso"
        placeholder="Selecione a política de reembolso"
        class="form--input form--input__select"
        id="form--reimbursement"
        @input="$emit('changeRefundPolicy', $event)"
      />

      <!-- LIMITE PARA REEMBOLSO -->
      <PmwFormInput
        :value="refundLimit"
        :error="errors.includes('LimiteHorasReembolso')"
        :required="!isNonRefundable"
        :infoIcon="true"
        :disabled="disabledPayment || isNonRefundable"
        data-error="LimiteHorasReembolso"
        tooltipMinWidth="181"
        infoText="Depois de atingido o limite de horas especificado neste campo, a tarifa reembolsável se torna não reembolsável."
        label="Limite para reembolso"
        placeholder="00"
        sufix="Horas"
        class="form--input form--input__refundLimit"
        id="form--limit-reimbursement"
        type="number"
        @input="$emit('changeRefundLimit', $event)"
      />

      <!-- PERCENTURAL DE PAGAMENTO PARCIAL EXIBIDO APENAS SE O TIPO DE PAGAMENTO FOR
      PRÉ PAGAMENTO E SE A COBRANÇA FOR PARCIAL -->
      <PmwFormInput
        v-if="type && type.value === 2 && charge && charge.value === 4"
        :value="partialPaymentPercentage"
        :hasMask="true"
        :disabled="disabledPayment"
        mask="##"
        label="Percentual de pagamento parcial"
        placeholder="00"
        sufix="%"
        class="form--input form--input__percentage"
        id="form--limit-reimbursement"
        @input="$emit('changePartialPaymentPercentage', $event)"
      />
    </div>

    <div v-if="hasPixEnabled" class="reference-tour" id="form--payments">
      <!-- TÍTULO -->
      <PmwHeading size="paragraph-80" weight="weight-light" class="form--title"
        >Deseja incluir pix em todas as tarifas?</PmwHeading
      >

      <div class="form--row mb-0">
        <!-- TODAS AS TARIFAS - PIX -->
        <div class="form--group-radio">
          <PmwParagraph
            size="paragraph-60"
            weight="medium"
            class="form--subtitle"
          >
            Pix
            <PmwInfoTooltip width="165px">
              <template #content>
                Ative ou desative pix em todas as tarifas
              </template>
            </PmwInfoTooltip>
          </PmwParagraph>

          <div class="form--row-checklist">
            <!-- ATIVO -->
            <PmwInputRadio
              :value="pix"
              :checked="pix"
              :inputValue="true"
              label="Ativo"
              name="paymentPix"
              @input="$emit('changePix', $event)"
            />

            <!-- INATIVO -->
            <PmwInputRadio
              :value="pix"
              :checked="!pix"
              :inputValue="false"
              label="Inativo"
              name="paymentPix"
              @input="$emit('changePix', $event)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- CDC -->
    <div class="cdc-section">
      <InputCheckbox
        :value="hasCdcEnabled"
        :checked="hasCdcEnabled"
        tooltipMinWidth="325"
        class="form-input-cdc"
        label="Ativar Direito de Arrependimento (CDC) para Hóspedes"
        infoIcon
        @change="$emit('changeCdcEnabled', $event)"
      >
        <template #info-tooltip-content>
          <span class="form-input-cdc--info-tooltip-content">
            Ao ativar esta opção, todas as reservas realizadas para este hotel
            <strong
              >poderão ser canceladas sem ônus em até 7 dias corridos após a sua
              confirmação</strong
            >, com estorno integral do valor pago, independentemente da política
            de cancelamento da regra tarifária.
          </span>
        </template>
      </InputCheckbox>
    </div>
  </div>
</template>

<script>
import PmwHeading from '@solucoes/components-lib-tmp/src/components/elements/PmwHeading'
import PmwParagraph from '@solucoes/components-lib-tmp/src/components/elements/PmwParagraph'
import PmwFormInput from '@solucoes/components-lib-tmp/src/components/patterns/PmwFormInput'
import PmwFormSelect from '@solucoes/components-lib-tmp/src/components/patterns/PmwFormSelect'
import PmwInputRadio from '@solucoes/components-lib-tmp/src/components/elements/PmwInputRadio'
import PmwInfoTooltip from '@solucoes/components-lib-tmp/src/components/patterns/PmwInfoTooltip'
import InputCheckbox from '@solucoes/components-lib-tmp/src/components/elements/InputCheckbox'

export default {
  name: 'BillingMethodAndRefundPolicy',
  components: {
    PmwHeading,
    PmwParagraph,
    PmwFormInput,
    PmwFormSelect,
    PmwInputRadio,
    PmwInfoTooltip,
    InputCheckbox
  },

  props: {
    type: {
      type: Object,
      default: () => {}
    },

    charge: {
      type: Object,
      default: () => {}
    },

    refundPolicy: {
      type: Object,
      default: () => {}
    },

    refundLimit: {
      type: [String, Number],
      default: null
    },

    partialPaymentPercentage: {
      type: [String, Number],
      default: null
    },

    gateway: {
      type: Object,
      default: () => {}
    },

    ticket: {
      type: Boolean,
      default: false
    },

    pix: {
      type: Boolean,
      default: false
    },

    /** Erros (array de erros que foram identificados pelo back-end) */
    errors: {
      type: Array,
      default: () => []
    },

    /** Lista de tipos de pagamento */
    paymentTypes: {
      type: Array,
      default: () => []
    },

    /** Lista de modalidades de cobranças */
    chargeList: {
      type: Array,
      default: () => []
    },

    /** Lista de políticas de reembolso */
    refundPolicyList: {
      type: Array,
      default: () => []
    },

    /** Se está com o código de defesa do consumidor habilitado */
    hasCdcEnabled: Boolean,

    /** Se o PIX estiver habilitado neste hotel */
    hasPixEnabled: Boolean
  },

  watch: {
    hasPixEnabled: {
      handler: function(newVal) {
        /** Se o pix não está ativado nas configurações do hotel, desativa para todas as tarifas */
        if (!newVal) {
          this.$emit('changePix', false)
        }
      }
    },

    /** Limpar refundLimit quando política for Não reembolsável */
    refundPolicy: {
      handler: function(newVal) {
        if (newVal?.value === 2) {
          this.$emit('changeRefundLimit', null)
        }
      },
      deep: true
    }
  },

  computed: {
    /** Verifica se a política é Não reembolsável ou tipo de pagamento é Sem pagamento */
    isNonRefundable() {
      return this.refundPolicy?.value === 2 || this.type?.value === 3
    },

    /** Opções de métodos de pagamentos */
    formattedPaymentTypes() {
      /** Se o gateway de pagamento for Mundipagg, permitir a opção "pré-pago" como método de pagamento */
      if (this.gateway?.value === 'MUNDIPAGG' || this.hasPixEnabled) {
        /** Retornar nova listagem de opções */
        return this.paymentTypes
      } else {
        return this.paymentTypes?.filter(
          (item) => item.value === 1 || item.value === 3
        )
      }
    },

    /** Opções de política de reembolso, por tipos de pagamento */
    formattedRefundPolicy() {
      /** Se for garantia */
      if (this.type?.value === 1) {
        /** Retornar nova listagem de opções */
        return this.refundPolicyList?.filter((item) => item.value === 1)
      } else {
        return this.refundPolicyList
      }
    },

    /** Opções de cobrança, por tipos de pagamento */
    formattedChargeList() {
      /** Se for garantia */
      if (this.type?.value === 1) {
        /** Retornar nova listagem de opções */
        return this.chargeList?.filter((item) => item.value === 2)
      } else {
        return this.chargeList
      }
    },

    /** Desativar os campos de pagamento */
    disabledPayment() {
      if (this.type?.value === 3) {
        /** Limpar os dados de pagamento */
        this.clearPaymentData()
        return true
      }
      return false
    }
  },

  methods: {
    /** Limpar os dados de pagamento */
    clearPaymentData() {
      this.$emit('changeCharge', null)
      this.$emit('changeRefundPolicy', null)
      this.$emit('changeRefundLimit', null)
    }
  }
}
</script>

<style lang="scss" scoped>
.reference-tour {
  position: relative;
}

.pmw-form-field {
  ::v-deep {
    .pmw-form-field--label {
      font-weight: font-weight('weight-medium');
    }
    .form-field--label-wrapper {
      line-height: 21px;
      margin-bottom: 3px;
    }
    .pmw-input {
      background: $white;
    }
    .pmw-input--clear {
      z-index: 0;
    }
    .pmw-input__focus {
      border-color: $pmw-blue;
    }

    .pmw-input--field {
      background: $pmw-white;

      &:focus {
        border-color: $pmw-blue;
      }
    }

    .pmw-custom-tooltip {
      line-height: 18px;
    }
  }
}

.form {
  &-input {
    &-cdc {
      margin-top: pmw-space(6);
    }
  }

  &--input {
    max-width: 176px;

    /** Alterar cores novas do select */
    ::v-deep {
      .pmw-select--button__caret-sort-up,
      .pmw-select--button__show {
        border-bottom-color: pmw-colors('blue-100');
      }
      .pmw-select--button:focus {
        border-color: pmw-colors('blue-100');
        border-bottom-color: pmw-colors('blue-100');
      }
      .pmw-select-options--item__hover:hover,
      .pmw-select-options--item__checked {
        background-color: pmw-colors('blue-100') !important;
      }

      .pmw-label {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    &__refundLimit {
      max-width: 181px;
    }

    &__percentage {
      max-width: 207px;
    }

    &__select {
      width: 229px;
      max-width: 229px;

      &.error {
        ::v-deep {
          .pmw-select--button {
            border-bottom-color: pmw-colors('error-100');
            border-bottom-width: 2px;
          }
        }
      }
    }
  }

  &--row {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 54px;
    margin-bottom: 54px;
  }

  &--title {
    color: pmw-colors('blue-100');
    font-weight: font-weight('weight-medium');
    margin-bottom: 24px;
    position: relative;
  }

  &--subtitle {
    font-size: font-size('size-medium');
    font-weight: font-weight('weight-medium');
    line-height: 21px;
    margin-bottom: 4px;
  }

  &--group-radio {
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    gap: 23px;

    p {
      width: 100%;
      margin-top: 0px;
      margin-bottom: 0px !important;
    }

    & > div {
      margin-bottom: 10px;
    }
  }

  &--row-checklist {
    display: flex;
    align-items: center;
    gap: 23px;
  }
}

.pmw-input-checkbox ::v-deep {
  span {
    overflow: initial;
  }
}

.pmw-input-radio ::v-deep {
  input:checked ~ {
    .pmw-input-radio--fake-radio {
      border-color: $pmw-blue;

      &:before {
        background-color: $pmw-blue;
      }
    }
  }
}
</style>
